import { useMemo } from 'react'
import { t } from '@lingui/macro'
import { ToggleButtonGroup as BaseToggleButtonGroup, ToggleButton, Tooltip, Box, Typography, Paper, styled, SvgIcon } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventIcon from '@mui/icons-material/Event'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import EventNoteIcon from '@mui/icons-material/EventNote'
import FilterListIcon from '@mui/icons-material/FilterList'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import { PlanningViewMode } from '../model/types'

const ToggleButtonGroup = styled(BaseToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButton-root': {
    padding: '6px',
    minWidth: '36px',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}))

export type ToggleType = 'planning-filter' | 'calendar-view'
type ToggleButtonItem = {
  'aria-label': string
  icon: typeof SvgIcon
  tooltip: string
  value: string
}

type ViewModeTogglerProps = {
  viewMode: PlanningViewMode
  onChange: (mode: PlanningViewMode) => void
  toggleType: ToggleType
  compact?: boolean
}

export function ViewModeToggler({ viewMode, onChange, toggleType, compact = false }: ViewModeTogglerProps) {
  const { t } = useTranslation()

  const toggleButtons: Record<ToggleType, ToggleButtonItem[]> = useMemo(
    () => ({
      'calendar-view': [
        {
          'aria-label': 'all items',
          icon: CalendarMonthIcon,
          tooltip: t('page.planning.viewMode.all.tooltip', { defaultValue: 'Show all items' }),
          value: 'all',
        },
        {
          'aria-label': 'shifts only',
          icon: EventNoteIcon,
          tooltip: t('page.planning.viewMode.shifts.tooltip', { defaultValue: 'Show shifts only' }),
          value: 'shifts',
        },
        {
          'aria-label': 'templates only',
          icon: EventRepeatIcon,
          tooltip: t('page.planning.viewMode.templates.tooltip', { defaultValue: 'Show templates only' }),
          value: 'templates',
        },
      ],
      'planning-filter': [
        {
          'aria-label': 'all plannings',
          icon: CalendarMonthIcon,
          tooltip: t('page.planning.viewMode.all.tooltip', { defaultValue: 'Show all plannings' }),
          value: 'all',
        },
        {
          'aria-label': 'active plannings',
          icon: EventAvailableIcon,
          tooltip: t('page.planning.viewMode.active.tooltip', { defaultValue: 'Show active plannings' }),
          value: 'active',
        },
        {
          'aria-label': 'upcoming plannings',
          icon: EventIcon,
          tooltip: t('page.planning.viewMode.upcoming.tooltip', { defaultValue: 'Show upcoming plannings' }),
          value: 'upcoming',
        },
        {
          'aria-label': 'past plannings',
          icon: EventBusyIcon,
          tooltip: t('page.planning.viewMode.past.tooltip', { defaultValue: 'Show past plannings' }),
          value: 'past',
        },
      ],
    }),
    [t]
  )

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 0.5, borderRadius: 2, width: compact ? 'auto' : '100%', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: compact ? 'row' : 'column', alignItems: 'center' }}>
        {!compact && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            {getTitleIcon(toggleType)}
            {getTitle(toggleType)}
          </Typography>
        )}
        <ToggleButtonGroup value={viewMode} exclusive onChange={(_, newMode) => newMode && onChange(newMode)} size="small" aria-label={viewMode}>
          {toggleButtons[toggleType].map((toggleButton) => (
            <Tooltip key={toggleButton.value} title={toggleButton.tooltip}>
              <ToggleButton value={toggleButton.value} aria-label={toggleButton['aria-label']}>
                <toggleButton.icon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Paper>
  )
}

function getTitle(toggleType: ToggleType) {
  switch (toggleType) {
    case 'calendar-view':
      return t`Calendar View`
    case 'planning-filter':
      return t`Planning Filter`
  }
}

function getTitleIcon(toggleType: ToggleType) {
  switch (toggleType) {
    case 'calendar-view':
      return <ViewWeekIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
    case 'planning-filter':
      return <FilterListIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
  }
}
